import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './MentorFeedbacksSubTab.css';

const API_URL = process.env.REACT_APP_API_URL || '';

function MentorFeedbacksSubTab() {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingFeedback, setEditingFeedback] = useState(null);
  const [formData, setFormData] = useState({ 
    name: '', 
    technology: '', 
    profile_url: '', 
    contact_username: '' 
  });

  useEffect(() => {
    fetchFeedbacks();
  }, []);

  const fetchFeedbacks = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/mentor-feedbacks`);
      setFeedbacks(response.data);
      setError(null);
    } catch (err) {
      setError('Ошибка при загрузке фитбэков менторов: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingFeedback) {
        await axios.put(`${API_URL}/api/mentor-feedbacks/${editingFeedback.id}`, formData);
      } else {
        await axios.post(`${API_URL}/api/mentor-feedbacks`, formData);
      }
      setShowForm(false);
      setEditingFeedback(null);
      setFormData({ name: '', technology: '', profile_url: '', contact_username: '' });
      fetchFeedbacks();
    } catch (err) {
      setError('Ошибка при сохранении фитбэка ментора: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleEdit = (feedback) => {
    setEditingFeedback(feedback);
    setFormData({
      name: feedback.name,
      technology: feedback.technology,
      profile_url: feedback.profile_url || '',
      contact_username: feedback.contact_username
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Вы уверены, что хотите удалить этот фитбэк ментора?')) {
      try {
        await axios.delete(`${API_URL}/api/mentor-feedbacks/${id}`);
        fetchFeedbacks();
      } catch (err) {
        setError('Ошибка при удалении фитбэка ментора: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingFeedback(null);
    setFormData({ name: '', technology: '', profile_url: '', contact_username: '' });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="loading">Загрузка...</div>;
  }

  return (
    <div className="mentor-feedbacks-sub-tab">
      <div className="tab-header">
        <h2>Фитбэки менторов</h2>
        {!showForm && (
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>
            + Добавить фитбэк
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <div className="form-container">
          <h3>{editingFeedback ? 'Редактировать фитбэк ментора' : 'Добавить новый фитбэк ментора'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">Имя ментора *</label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                maxLength={255}
                placeholder="Введите имя ментора"
              />
            </div>
            <div className="form-group">
              <label htmlFor="technology">Технология/стэк *</label>
              <input
                type="text"
                id="technology"
                value={formData.technology}
                onChange={(e) => setFormData({ ...formData, technology: e.target.value })}
                required
                maxLength={255}
                placeholder="Например: Python Backend Developer"
              />
            </div>
            <div className="form-group">
              <label htmlFor="profile_url">Ссылка на профиль</label>
              <input
                type="url"
                id="profile_url"
                value={formData.profile_url}
                onChange={(e) => setFormData({ ...formData, profile_url: e.target.value })}
                maxLength={500}
                placeholder="https://..."
              />
            </div>
            <div className="form-group">
              <label htmlFor="contact_username">Контактный username *</label>
              <input
                type="text"
                id="contact_username"
                value={formData.contact_username}
                onChange={(e) => setFormData({ ...formData, contact_username: e.target.value })}
                required
                maxLength={255}
                placeholder="@username"
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingFeedback ? 'Сохранить' : 'Создать'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Отмена
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Имя</th>
              <th>Технология</th>
              <th>Ссылка на профиль</th>
              <th>Контактный username</th>
              <th>Дата создания</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {feedbacks.length === 0 ? (
              <tr>
                <td colSpan="7" className="empty-state">
                  Нет фитбэков менторов. Добавьте первый фитбэк.
                </td>
              </tr>
            ) : (
              feedbacks.map((feedback) => (
                <tr key={feedback.id}>
                  <td>{feedback.id}</td>
                  <td>{feedback.name}</td>
                  <td>{feedback.technology}</td>
                  <td>
                    {feedback.profile_url ? (
                      <a href={feedback.profile_url} target="_blank" rel="noopener noreferrer">
                        {feedback.profile_url.length > 50 ? feedback.profile_url.substring(0, 50) + '...' : feedback.profile_url}
                      </a>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>{feedback.contact_username}</td>
                  <td>{formatDate(feedback.created_at)}</td>
                  <td>
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(feedback)}
                      title="Редактировать"
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(feedback.id)}
                      title="Удалить"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default MentorFeedbacksSubTab;
